import Image from '@tiptap/extension-image';
import { Color, TextStyle } from '@tiptap/extension-text-style';
import { Placeholder } from '@tiptap/extensions';
import { EditorContent, ReactNodeViewRenderer, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { api } from '@/lib/api';
import { ImageRow } from '@/components/tiptap-image-row';
import { TranslationIds } from '@/components/tiptap-translation-ids';
import { ImageView } from '@/components/tiptap-image-view';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown, Heading2, ImagePlus, Pilcrow, Type } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

const EditableImage = Image.extend({
  addNodeView() {
    return ReactNodeViewRenderer(ImageView);
  },
});

export function RichTextEditor({
  value,
  onChange,
}: {
  value?: Record<string, unknown>;
  onChange: (value: Record<string, unknown>) => void;
}) {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const imageRowInputRef = useRef<HTMLInputElement>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [, refreshToolbar] = useState(0);
  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit,
      TranslationIds,
      TextStyle,
      Color,
      EditableImage,
      ImageRow,
      Placeholder.configure({ placeholder: 'Schrijf de inhoud van deze case…' }),
    ],
    content: value,
    onUpdate: ({ editor: currentEditor }) => onChange(currentEditor.getJSON()),
    onSelectionUpdate: () => refreshToolbar(version => version + 1),
  });

  useEffect(() => {
    if (editor && value && JSON.stringify(editor.getJSON()) !== JSON.stringify(value))
      editor.commands.setContent(value);
  }, [editor, value]);

  if (!editor) return null;

  const uploadImage = async (file: File) => {
    const body = new FormData();
    body.append('file', file);
    return api.postForm<{ id: string; originalFilename: string }>('/media', body);
  };
  const insertImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploadingImage(true);
    try {
      const asset = await uploadImage(file);
      editor
        .chain()
        .focus()
        .setImage({ src: `/api/public/media/${asset.id}?width=2000`, alt: asset.originalFilename })
        .run();
      toast.success('Afbeelding ingevoegd.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Afbeelding invoegen is niet gelukt.');
    } finally {
      setUploadingImage(false);
    }
  };
  const insertImageRow = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (files.length !== 2) {
      toast.error('Kies precies twee afbeeldingen voor een tweekolomsblok.');
      return;
    }
    setUploadingImage(true);
    try {
      const assets = await Promise.all(files.map(uploadImage));
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'imageRow',
          attrs: {
            images: assets.map(asset => ({
              src: `/api/public/media/${asset.id}?width=1000`,
              alt: asset.originalFilename,
            })),
          },
        })
        .run();
      toast.success('Twee afbeeldingen ingevoegd.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Afbeeldingen invoegen is niet gelukt.');
    } finally {
      setUploadingImage(false);
    }
  };
  const buttonClass = (active: boolean) => `rounded px-2 py-1 text-sm hover:bg-muted ${active ? 'bg-muted' : ''}`;
  const inlineIcon = 'mr-1 inline-block size-4 -translate-y-0.5 align-middle';

  return (
    <div className="rounded-md border">
      <div className="sticky top-0 z-20 flex flex-wrap gap-1 border-b bg-background/95 p-2 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <button
          type="button"
          title="Tussenkop"
          aria-label="Tussenkop"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={buttonClass(editor.isActive('heading', { level: 2 }))}
        >
          <Heading2 className={inlineIcon} />
          Titel
        </button>
        <button
          type="button"
          title="Paragraaf"
          aria-label="Paragraaf"
          onClick={() => editor.chain().focus().setParagraph().run()}
          className={buttonClass(editor.isActive('paragraph'))}
        >
          <Pilcrow className={inlineIcon} />
          Paragraaf
        </button>
        <span className="mx-1 w-px bg-border" />
        <button
          type="button"
          title="Vet"
          aria-label="Vet"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={buttonClass(editor.isActive('bold'))}
        >
          <b>B</b>
        </button>
        <button
          type="button"
          title="Cursief"
          aria-label="Cursief"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={buttonClass(editor.isActive('italic'))}
        >
          <span className="inline-block translate-y-px font-serif text-base italic">I</span>
        </button>
        <button
          type="button"
          title="Opsomming"
          aria-label="Opsomming"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={buttonClass(editor.isActive('bulletList'))}
        >
          <img
            src="/FAME-star.svg"
            alt=""
            className="mr-1 inline-block size-3 -translate-y-0.5 align-middle brightness-0"
          />
          Lijst
        </button>
        <span className="mx-1 w-px bg-border" />
        <button
          type="button"
          disabled={editor.isActive('heading')}
          onClick={() => editor.chain().focus().setColor('#939393').run()}
          className={buttonClass(editor.isActive('textStyle', { color: '#939393' }))}
        >
          <Type className={inlineIcon} />
          Standaard
        </button>
        <button
          type="button"
          disabled={editor.isActive('heading')}
          onClick={() => editor.chain().focus().setColor('#222222').run()}
          className={buttonClass(editor.isActive('textStyle', { color: '#222222' }))}
        >
          Donker
        </button>
        <span className="mx-1 w-px bg-border" />
        <input
          ref={imageInputRef}
          className="hidden"
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          onChange={insertImage}
        />
        <input
          ref={imageRowInputRef}
          className="hidden"
          type="file"
          multiple
          accept="image/png,image/jpeg,image/gif,image/webp"
          onChange={insertImageRow}
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" disabled={uploadingImage} className={buttonClass(false)}>
              <ImagePlus className={inlineIcon} />
              {uploadingImage ? 'Uploaden…' : 'Afbeelding'}
              <ChevronDown className={inlineIcon.replace('mr-1', 'ml-1 mr-0')} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem disabled={uploadingImage} onSelect={() => imageInputRef.current?.click()}>
              Eén beeld over de volle breedte
            </DropdownMenuItem>
            <DropdownMenuItem disabled={uploadingImage} onSelect={() => imageRowInputRef.current?.click()}>
              Twee beelden naast elkaar
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <EditorContent
        editor={editor}
        className="min-h-56 p-4 [&_.tiptap]:min-h-48 [&_.tiptap]:font-['Plus_Jakarta_Sans'] [&_.tiptap]:text-[16px] [&_.tiptap]:text-[#939393] [&_.tiptap_h1]:mt-[30px] [&_.tiptap_h1]:font-['Prata'] [&_.tiptap_h1]:text-[25px] [&_.tiptap_h1]:font-normal [&_.tiptap_h1]:leading-tight [&_.tiptap_h1]:text-[#012438] [&_.tiptap_h2]:mt-[30px] [&_.tiptap_h2]:font-['Prata'] [&_.tiptap_h2]:text-[25px] [&_.tiptap_h2]:font-normal [&_.tiptap_h2]:leading-tight [&_.tiptap_h2]:text-[#012438] [&_.tiptap_p]:my-3 [&_.tiptap_h1+p]:mt-2 [&_.tiptap_h2+p]:mt-2 [&_.tiptap_ul]:my-3 [&_.tiptap_ul]:list-none [&_.tiptap_ul]:pl-0 [&_.tiptap_ul>li]:relative [&_.tiptap_ul>li]:pl-6 [&_.tiptap_ul>li]:before:absolute [&_.tiptap_ul>li]:before:left-0 [&_.tiptap_ul>li]:before:top-1.5 [&_.tiptap_ul>li]:before:size-3 [&_.tiptap_ul>li]:before:bg-[url('/FAME-star.svg')] [&_.tiptap_ul>li]:before:bg-contain [&_.tiptap_ul>li]:before:bg-no-repeat [&_.tiptap_ol]:my-3 [&_.tiptap_ol]:list-decimal [&_.tiptap_ol]:pl-6 [&_.tiptap_li]:my-0.5 [&_.tiptap_li>p]:my-0 [&_.tiptap_img]:my-4 [&_.tiptap_img]:max-w-full [&_.tiptap_img]:rounded-md [&_.tiptap_.image-row]:my-4 [&_.tiptap_.image-row]:grid [&_.tiptap_.image-row]:grid-cols-2 [&_.tiptap_.image-row]:gap-4 [&_.tiptap_.image-row_img]:my-0 [&_.tiptap_.image-row_img]:h-full [&_.tiptap_.image-row_img]:w-full [&_.tiptap_.image-row_img]:object-cover [&_.tiptap]:outline-none"
      />
    </div>
  );
}
